import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { adminApi } from '../api/adminApi';
import toast, { Toaster } from 'react-hot-toast';
import PageHeader from '../components/common/PageHeader';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const BusinessForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    name: '',
    type: 'retail',
    isActive: true,
    subscriptionPlan: '',
    subscriptionExpiry: ''
  });
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch plans for dropdown
        const plansRes = await adminApi.getPlans();
        setPlans(plansRes.data.data || []);
        
        if (location.state?.business) {
          populateForm(location.state.business);
        } else if (id) {
          const res = await adminApi.getAllBusinesses();
          const business = res.data.data.find(b => b._id === id);
          if (business) {
            populateForm(business);
          } else {
            toast.error('Business not found');
            navigate('/businesses');
          }
        }
      } catch (error) {
        toast.error('Failed to load data');
        navigate('/businesses');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, navigate, location.state]);

  const populateForm = (business) => {
    setFormData({
      name: business.name || '',
      type: business.type || 'retail',
      isActive: business.isActive !== false,
      subscriptionPlan: business.subscriptionPlan?._id || business.subscriptionPlan || '',
      subscriptionExpiry: business.subscriptionExpiry 
        ? new Date(business.subscriptionExpiry).toISOString().split('T')[0] 
        : ''
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await adminApi.updateBusiness(id, formData);
      toast.success('Business updated successfully');
      setTimeout(() => navigate('/businesses'), 1000);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update business');
    }
  };

  if (loading) {
    return <div className="min-h-screen p-12 text-center text-gray-500 font-bold">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-transparent pb-12">
      <Toaster position="top-right" />
      
      <PageHeader 
        title="Edit Business"
        subtitle="Manage business details and subscriptions."
        backLink="/businesses"
      />

      <main className="max-w-full mx-auto">
        <div className="bg-white rounded-[14px] shadow-[0_8px_30px_rgb(0,0,0,0.02)] overflow-hidden border-t-[6px] border-red-600">
          <form onSubmit={handleSave} className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              
              {/* Business Details */}
              <div className="space-y-5">
                <h3 className="text-xs font-extrabold text-gray-400 uppercase tracking-widest flex items-center">
                  <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-2">1</span>
                  Business Information
                </h3>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Business Name</label>
                  <input 
                    required type="text" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 font-bold text-gray-900 outline-none transition-all" 
                    value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Business Type</label>
                  <select 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white font-bold text-gray-900 outline-none transition-all focus:ring-2 focus:ring-red-500"
                    value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}
                  >
                    <option value="retail">Retail</option>
                    <option value="wholesale">Wholesale</option>
                    <option value="services">Services</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="pt-4 flex flex-col space-y-4">
                  <label className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 cursor-pointer hover:border-red-200 transition-colors">
                    <div>
                      <span className="text-sm font-extrabold text-gray-900 block">Active Status</span>
                      <span className="text-xs font-medium text-gray-500">Business can log in and use features</span>
                    </div>
                    <div className="relative flex items-center">
                      <input 
                        type="checkbox" 
                        checked={formData.isActive} 
                        onChange={e => setFormData({...formData, isActive: e.target.checked})} 
                        className="peer sr-only" 
                      />
                      <div className={`w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-red-600 transition-all after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5 peer-checked:after:border-white`}></div>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Subscription & Plan */}
              <div className="space-y-5 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                <h3 className="text-xs font-extrabold text-gray-400 uppercase tracking-widest flex items-center mb-1">
                  <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-2">2</span>
                  Subscription Management
                </h3>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Active Plan</label>
                  <select 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white font-bold text-gray-900 outline-none transition-all focus:ring-2 focus:ring-red-500"
                    value={formData.subscriptionPlan} onChange={e => setFormData({...formData, subscriptionPlan: e.target.value})}
                  >
                    <option value="">No Plan</option>
                    {plans.map(plan => (
                      <option key={plan._id} value={plan._id}>{plan.name} (₹{plan.priceMonthly}/mo)</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Plan Expiry Date</label>
                  <input 
                    type="date" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white focus:bg-white focus:ring-2 focus:ring-red-500 font-bold text-gray-900 outline-none transition-all" 
                    value={formData.subscriptionExpiry} onChange={e => setFormData({...formData, subscriptionExpiry: e.target.value})} 
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank for lifetime validity.</p>
                </div>
              </div>
              
            </div>
            
            <div className="pt-6 border-t border-gray-100 flex justify-end space-x-4">
              <button 
                type="button" 
                onClick={() => navigate('/businesses')} 
                className="px-8 py-3 rounded-full text-sm font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-8 py-3 rounded-full text-sm font-bold bg-red-600 text-white hover:bg-red-700 shadow-md transition-all transform hover:-translate-y-0.5 hover:shadow-lg flex items-center"
              >
                Save Changes
                <ArrowLeftIcon className="h-4 w-4 ml-2 transform rotate-180" />
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default BusinessForm;
